import { noop } from 'lodash';
import { useMemo } from 'react';

import { MedicalDataAccessAuthorization } from '@dreryk/egabinet-patient-frontend-api';

import { dateFormatter } from '@dreryk/react-common';
import {
   AlignItems,
   createDynamicColumnsWidth,
   EmptyStatePlaceholder,
   JustifyContent,
   Table,
   TableColumnConfiguration,
   TextPlaceholder,
} from '@dreryk/react-components';

import { patientConsentsAndAuthorizationsTranslationKeys } from '../../../translations';
import { CreateMedicalDataAccessAuthorizationAction } from '../CreateMedicalDataAccessAuthorizationAction/CreateMedicalDataAccessAuthorizationAction';

import { StyledMedicalDataAccessAuthorizationPlaceholder } from './MedicalDataAccessAuthorizationPlaceholder.styled';
import { ActionsCell } from './TableCells/ActionsCell';
import { AuthorizationCell } from './TableCells/AuthorizationCell';
import { PatientCell } from './TableCells/PatientCell';
import { PhoneCell } from './TableCells/PhoneCell';

type MedicalDataAccessAuthorizationTableProps = {
   readonly data: MedicalDataAccessAuthorization[];
   readonly patientId: number;
};

const createColumns = (): TableColumnConfiguration<MedicalDataAccessAuthorization>[] => {
   const columnsWidth = createDynamicColumnsWidth({
      patientName: '100%',
      phoneNumber: '215px',
      authorizations: '320px',
      enterData: '205px',
      actions: '89px',
   });

   return [
      {
         name: 'patientName',
         accessor: noop,
         label: patientConsentsAndAuthorizationsTranslationKeys.authorizations.table.authorizedPerson,
         width: columnsWidth.patientName,
         renderer: PatientCell,
         sortingDisabled: true,
      },
      {
         name: 'phoneNumber',
         accessor: 'phoneNumber',
         label: patientConsentsAndAuthorizationsTranslationKeys.authorizations.table.phoneNumber,
         width: columnsWidth.phoneNumber,
         renderer: PhoneCell,
         sortingDisabled: true,
         placeholder: TextPlaceholder.NO_DATA,
      },
      {
         name: 'authorizations',
         accessor: 'forInformation',
         label: patientConsentsAndAuthorizationsTranslationKeys.authorizations.table.authorizations,
         width: columnsWidth.authorizations,
         renderer: AuthorizationCell,
         sortingDisabled: true,
      },
      {
         name: 'enterDate',
         accessor: 'enterDate',
         label: patientConsentsAndAuthorizationsTranslationKeys.authorizations.table.authorizationDate,
         width: columnsWidth.enterData,
         formatter: dateFormatter,
         sortingDisabled: true,
      },
      {
         name: 'actions',
         accessor: noop,
         label: '',
         width: columnsWidth.actions,
         justifyContent: JustifyContent.END,
         alignItems: AlignItems.CENTER,
         sortingDisabled: true,
         renderer: ActionsCell,
      },
   ];
};

const MedicalDataAccessAuthorizationTable = (props: MedicalDataAccessAuthorizationTableProps): JSX.Element => {
   const columns = useMemo(() => createColumns(), []);
   const dataConfiguration = useMemo(() => ({ data: props.data }), [props.data]);

   return (
      <Table<MedicalDataAccessAuthorization>
         testId={'medicalDataAccessAuthorization'}
         columns={columns}
         dataConfiguration={dataConfiguration}
         keyExtractor={'id'}
         bottomElement={<CreateMedicalDataAccessAuthorizationAction patientId={props.patientId} withIcon />}
         placeholder={
            <StyledMedicalDataAccessAuthorizationPlaceholder>
               <EmptyStatePlaceholder
                  heading={patientConsentsAndAuthorizationsTranslationKeys.authorizations.noAuthorizations}
                  subheading={
                     patientConsentsAndAuthorizationsTranslationKeys.authorizations.patientDidNotGiveAuthorization
                  }
                  fullLength
                  testId={'patientDidNotGiveAuthorization'}
               >
                  <CreateMedicalDataAccessAuthorizationAction patientId={props.patientId} />
               </EmptyStatePlaceholder>
            </StyledMedicalDataAccessAuthorizationPlaceholder>
         }
      />
   );
};

export { MedicalDataAccessAuthorizationTable };
